import React, { useEffect, useState } from 'react';
import {
  Card,
  Box,
  Typography,
  TablePagination,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useDashboardFilters } from 'contexts/DashboardFilterContext';
import { buildFilterBody } from './utils';
import axiosInstance from "@/utils/axios";
import { useIntl } from "react-intl";

// ─── Types ────────────────────────────────────────────────────────────────────

type SortKey = 
  | 'eventName'
  | 'competitionName'
  | 'season'
  | 'totalPublications'
  | 'totalViews'
  | 'totalLikes'
  | 'totalShares'
  | 'totalDuration';

type SortDir = 'asc' | 'desc';

interface EventRow {
  eventName: string;
  competitionName: string;
  season: string;
  totalPublications: number;
  totalViews: number;
  totalLikes: number;
  totalShares: number;
  totalDuration: string;
}

// ─── Layout Dimensioni e Allineamenti ─────────────────────────────────────────
const colLayout = {
  rank: { width: 50, display: 'flex', justifyContent: 'center', flexShrink: 0 },
  eventName: { flex: 2, minWidth: 200, display: 'flex', justifyContent: 'flex-start', flexShrink: 0 },
  competitionName: { flex: 1.5, minWidth: 150, display: 'flex', justifyContent: 'flex-start', flexShrink: 0 },
  season: { flex: 0.8, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  totalPublications: { flex: 1, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  totalViews: { flex: 1, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  totalLikes: { flex: 1, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  totalShares: { flex: 1, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  totalDuration: { flex: 1, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
};

// ─── Cells ─────────────────────────────────────────────────────────────────────

const RankCell = ({ rank }: { rank: number }) => (
  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary' }}>
    {rank}
  </Typography>
);

const EventCell = ({ eventName }: { eventName: string }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: 'text.primary' }}>
      {eventName}
    </Typography>
  </Box>
);

const StatCell = ({ value, align = 'right' }: { value: number | string; align?: 'left' | 'right' }) => (
  <Typography 
    sx={{ 
      fontSize: '0.8125rem', 
      fontWeight: 600, 
      fontVariantNumeric: 'tabular-nums',
      textAlign: align 
    }}
  >
    {typeof value === 'number' 
      ? (formatCompactNumber(value)) 
      : value}
  </Typography>
);

const formatCompactNumber = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace('.0', '')}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1).replace('.0', '')}K`;
  }
  return value.toString();
};

// ─── Main Component ────────────────────────────────────────────────────────────

export default function MostSharedEventPlatformChart() {
  const [chartData, setChartData] = useState<EventRow[]>([]);
  const { contentType, competition, season, dateRange, updateDashboard, filterLoaded } = useDashboardFilters();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('totalShares'); 
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const intl = useIntl(); 

  const handleSort = (key: SortKey) => {
    setSortDir(prev => sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'desc');
    setSortKey(key);
  };

  const sorted = [...chartData].sort((a, b) => {
    const valA = a[sortKey];
    const valB = b[sortKey];
    
    if (typeof valA === 'string' && typeof valB === 'string') {
      return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    }
    
    const diff = Number(valA ?? 0) - Number(valB ?? 0);
    return sortDir === 'asc' ? diff : -diff;
  });

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const sortableCols = [
    { key: 'competitionName', label: 'Competition', align: 'left' },
    { key: 'season', label: 'Season', align: 'right' },
    { key: 'totalPublications', label: 'Publications', align: 'right' },
    { key: 'totalViews', label: 'Views', align: 'right' },
    { key: 'totalLikes', label: 'Likes', align: 'right' },
    { key: 'totalShares', label: 'Shares', align: 'right' },
    { key: 'totalDuration', label: 'Duration', align: 'right' },
  ] as const;

  useEffect(() => {
    if (filterLoaded) {
      let cancelled = false;

      const fetchData = async () => {      
        try {
          setIsLoading(true);
          setError(null);

          const body = buildFilterBody(
            contentType ?? [],
            competition ?? [],
            season ?? [],
            dateRange
          );

          // Controlla che anche questo endpoint sia corretto nel tuo backend,
          // se l'hai cambiato per riflettere "Event" ricordati di aggiornarlo qui
          const res = await axiosInstance.post('/dashboard/getMostSharedClipPlatformChart', body);

          if (!cancelled) {
            setChartData(res.data || []);
            setPage(0); 
          }
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to load analytics data');
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      };

      fetchData();
      return () => { cancelled = true; };
    }
  }, [updateDashboard, filterLoaded, contentType, competition, season, dateRange]);

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px' }} data-tour={"dashboard_match_engagement_ranking"}>
      
      {/* HEADER */}
      <Box px={3} pt={2.5} pb={2}>
        <Typography variant="h3" fontWeight={700} sx={{ fontSize: '1.25rem' }}>
          Most Shared Events Overview
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Ranking of events based on social engagement metrics
        </Typography>
      </Box>

      <Divider />

      {/* CONTENITORE TABELLA SCROLLABILE REATTIVA */}
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1000 }}>

          {/* HEADERS DELLA TABELLA (Sempre visibili) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 3,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={colLayout.rank}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>#</Typography>
            </Box>
            
            <Box
              sx={{ 
                ...colLayout.eventName, 
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { color: 'text.primary' }
              }}
              onClick={() => handleSort('eventName')}
            >
               <Typography 
                 sx={{ 
                   fontSize: '0.7rem', 
                   fontWeight: 700, 
                   color: sortKey === 'eventName' ? 'text.primary' : 'text.secondary',
                   textTransform: 'uppercase',
                   display: 'flex',
                   alignItems: 'center'
                 }}
               >
                 Event {sortKey === 'eventName' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
               </Typography>
            </Box>

            {sortableCols.map(col => {
              const isActive = sortKey === col.key;
              return (
                <Box
                  key={col.key}
                  sx={{ 
                    ...colLayout[col.key as keyof typeof colLayout], 
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { color: 'text.primary' }
                  }}
                  onClick={() => handleSort(col.key as SortKey)}
                >
                  <Typography 
                    sx={{ 
                      fontSize: '0.7rem', 
                      fontWeight: 700, 
                      color: isActive ? 'text.primary' : 'text.secondary',
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {col.label} {isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* RIGHE DATI DELLA TABELLA / STATI DI CARICAMENTO E NO-DATA */}
          <Box 
            sx={{ 
              height: 440, 
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {isLoading ? (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flexGrow={1} gap={2}>
                <CircularProgress size={28} thickness={5} />
                <Typography variant="body2" color="text.secondary">
                  Loading data...
                </Typography>
              </Box>
            ) : error ? (
              <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} px={3}>
                <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                  {error}
                </Typography>
              </Box>
            ) : chartData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  No data available to display
                </Typography>
              </Box>
            ) : (
              paginated.map((eventItem, idx) => {
                const rank = page * rowsPerPage + idx + 1;

                return (
                  <Box
                    key={`${eventItem.eventName}-${idx}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 3,
                      py: 1.75,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:last-child': { borderBottom: 0 }
                    }}
                  >
                    <Box sx={colLayout.rank}>
                      <RankCell rank={rank} />
                    </Box>

                    <Box sx={colLayout.eventName}>
                      <EventCell eventName={eventItem.eventName} />
                    </Box>

                    <Box sx={colLayout.competitionName}>
                      <StatCell value={eventItem.competitionName} align="left" />
                    </Box>

                    <Box sx={colLayout.season}>
                      <StatCell value={eventItem.season} />
                    </Box>

                    <Box sx={colLayout.totalPublications}>
                      <StatCell value={eventItem.totalPublications} />
                    </Box>

                    <Box sx={colLayout.totalViews}>
                      <StatCell value={eventItem.totalViews} />
                    </Box>

                    <Box sx={colLayout.totalLikes}>
                      <StatCell value={eventItem.totalLikes} />
                    </Box>

                    <Box sx={colLayout.totalShares}>
                      <StatCell value={eventItem.totalShares} />
                    </Box>

                    <Box sx={colLayout.totalDuration}>
                      <StatCell value={eventItem.totalDuration} />
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          {/* PAGINAZIONE IN FONDO ALLA CARD */}
          <TablePagination
            component="div"
            count={sorted.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            onRowsPerPageChange={() => {}}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />

        </Box>
      </Box>
    </Card>
  );
}